<template>
  <transition name="modal-fade">
    <div class="modal-root" v-if="open" @mousedown.self="close">
      <div class="modal-card" @mousedown.stop>
        <header class="modal-head">
          <div class="head-icon">
            <i class="bi bi-stars" style="font-size:20px" aria-hidden="true"></i>
          </div>
          <div class="head-text">
            <h2 class="modal-title">AI suggestion</h2>
            <p class="modal-subtitle">Look up influencer info via Perplexity AI — preview only.</p>
          </div>
          <button class="head-close" @click="close" title="Close (Esc)">
            <i class="bi bi-x" style="font-size:18px" aria-hidden="true"></i>
          </button>
        </header>

        <div class="modal-body">
          <section class="step">
            <div class="step-head">
              <span class="step-badge">1</span>
              <h3 class="step-title">Input</h3>
              <span class="step-desc">Provide a name and/or known social channels.</span>
            </div>
            <div class="step-body">
              <div class="form-group">
                <label class="form-label">Name / nickname</label>
                <input v-model="local.name" type="text" class="form-control" placeholder="e.g. Friz">
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">First name</label>
                  <input v-model="local.firstname" type="text" class="form-control">
                </div>
                <div class="form-group">
                  <label class="form-label">Last name</label>
                  <input v-model="local.lastname" type="text" class="form-control">
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Channels</label>
                <div class="channels-list">
                  <div
                    v-for="(ch, idx) in local.channels"
                    :key="idx"
                    class="channel-row"
                  >
                    <div class="channel-icon" :class="ch.platform ? 'has-platform' : ''">
                      <img v-if="ch.platform" :src="platformIconSrc(ch.platform)" :alt="ch.platform"/>
                      <span v-else class="dot">?</span>
                    </div>
                    <input
                      type="text"
                      class="form-control"
                      v-model="ch.url"
                      @input="onChannelInput(idx)"
                      placeholder="https://instagram.com/username"
                    >
                    <button class="remove-btn" @click="removeChannel(idx)" title="Remove">
                      <i class="bi bi-x" aria-hidden="true"></i>
                    </button>
                  </div>
                  <button class="add-channel-btn" @click="addChannel">
                    <i class="bi bi-plus" aria-hidden="true"></i>
                    Add channel
                  </button>
                </div>
              </div>

              <div class="form-group model-group">
                <label class="form-label">
                  Model
                  <span class="model-cost" :class="'cost-' + currentModel.cost">{{ currentModel.costLabel }}</span>
                </label>
                <div class="model-options">
                  <label
                    v-for="m in models"
                    :key="m.value"
                    class="model-option"
                    :class="{ 'is-active': local.model === m.value }"
                  >
                    <input
                      type="radio"
                      :value="m.value"
                      v-model="local.model"
                    >
                    <span class="model-option-name">{{ m.label }}</span>
                    <span class="model-option-badge" v-if="m.badge">{{ m.badge }}</span>
                  </label>
                </div>
                <p class="model-hint">{{ currentModel.description }}</p>
              </div>

              <button
                class="btn-search"
                @click="search"
                :disabled="!canSearch || loading"
              >
                <span v-if="!loading" class="btn-content">
                  <i class="bi bi-stars" aria-hidden="true"></i>
                  Search with AI
                </span>
                <span v-else class="btn-content">
                  <span class="spinner"></span> Searching…
                </span>
              </button>
              <p class="hint" v-if="!canSearch">Provide at least a name, first/last name or one channel.</p>
            </div>
          </section>

          <section class="step step-results" v-if="result || error">
            <div class="step-head">
              <span class="step-badge step-badge-2">2</span>
              <h3 class="step-title">Result</h3>
              <span class="step-desc">Preview of what Perplexity returned.</span>
            </div>

            <div class="result-error" v-if="error">
              <i class="bi bi-exclamation-triangle-fill" style="font-size:18px" aria-hidden="true"></i>
              <div>
                <strong>Search failed</strong>
                <p>{{ error }}</p>
              </div>
            </div>

            <div class="result-grid" v-if="result && result.influencer">
              <div class="result-card result-card-main">
                <div class="result-card-head">
                  <span class="card-eyebrow">Person</span>
                </div>
                <h4 class="result-nickname" v-if="result.influencer.nickname">{{ result.influencer.nickname }}</h4>
                <p class="result-realname" v-if="result.influencer.firstname || result.influencer.lastname">
                  {{ [result.influencer.firstname, result.influencer.lastname].filter(Boolean).join(' ') }}
                </p>
                <div class="result-attrs">
                  <span class="attr" v-if="result.influencer.location">
                    <i class="bi bi-geo-alt-fill" aria-hidden="true"></i>
                    {{ result.influencer.location }}
                  </span>
                  <span class="attr" v-if="result.influencer.age">
                    <i class="bi bi-calendar-fill" aria-hidden="true"></i>
                    {{ result.influencer.age }} y/o
                  </span>
                  <span class="attr" v-if="genderLabel">
                    <i class="bi bi-gender-ambiguous" aria-hidden="true"></i>
                    {{ genderLabel }}
                  </span>
                  <span class="attr" v-if="result.influencer.lang">
                    <i class="bi bi-globe2" aria-hidden="true"></i>
                    {{ result.influencer.lang.toUpperCase() }}
                  </span>
                </div>
              </div>

              <div class="result-card" v-if="result.influencer.description">
                <span class="card-eyebrow">Bio</span>
                <p class="bio">{{ result.influencer.description }}</p>
              </div>

              <div class="result-card" v-if="result.influencer.category_suggestion">
                <span class="card-eyebrow">Suggested categories</span>
                <div class="chip-row">
                  <span class="chip chip-blue" v-for="c in splitCsv(result.influencer.category_suggestion)" :key="'cat'+c">{{ c }}</span>
                </div>
              </div>

              <div class="result-card" v-if="result.influencer.tags">
                <span class="card-eyebrow">Tags</span>
                <div class="chip-row">
                  <span class="chip" v-for="t in splitCsv(result.influencer.tags)" :key="'tag'+t">#{{ t }}</span>
                </div>
              </div>

              <div class="result-card" v-if="(result.influencer.channels || []).length">
                <span class="card-eyebrow">Channels</span>
                <div class="channels-result">
                  <a
                    v-for="(ch, idx) in result.influencer.channels"
                    :key="'ch' + idx"
                    class="channel-pill"
                    :href="ch.url"
                    target="_blank"
                    rel="noopener"
                  >
                    <img v-if="ch.platform" :src="platformIconSrc(ch.platform)" :alt="ch.platform"/>
                    <div class="channel-pill-text">
                      <span class="channel-platform">{{ ch.platform }}</span>
                      <span class="channel-username">@{{ ch.username || extractUsername(ch.url) }}</span>
                    </div>
                    <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                  </a>
                </div>
              </div>

              <div class="result-card" v-if="result.influencer.email || result.influencer.site">
                <span class="card-eyebrow">Contact</span>
                <div class="kv-list">
                  <div class="kv" v-if="result.influencer.email">
                    <span class="kv-key">Email</span>
                    <a class="kv-val" :href="'mailto:' + result.influencer.email">{{ result.influencer.email }}</a>
                  </div>
                  <div class="kv" v-if="result.influencer.site">
                    <span class="kv-key">Website</span>
                    <a class="kv-val" :href="result.influencer.site" target="_blank">{{ result.influencer.site }}</a>
                  </div>
                </div>
              </div>

              <div class="result-card result-card-full" v-if="(result.citations || []).length">
                <span class="card-eyebrow">Sources ({{ result.citations.length }})</span>
                <ul class="citations">
                  <li v-for="(c, idx) in result.citations" :key="'cit' + idx">
                    <a :href="c" target="_blank" rel="noopener">{{ c }}</a>
                  </li>
                </ul>
              </div>
            </div>

            <div class="result-empty" v-else-if="result && !result.influencer">
              <i class="bi bi-search" style="font-size:32px" aria-hidden="true"></i>
              <p>No information found for the given input.</p>
            </div>
          </section>
        </div>

        <footer class="modal-foot">
          <div class="foot-info">
            <template v-if="result && result.influencer">
              <i class="bi bi-info-circle-fill" aria-hidden="true"></i>
              <span>Applying overwrites existing fields. Channels land in a <strong>preview</strong> section.</span>
            </template>
            <template v-else>
              <i class="bi bi-info-circle-fill" aria-hidden="true"></i>
              <span>Provide input above and run the search.</span>
            </template>
            <span class="usage" v-if="result && result.usage">
              · {{ result.usage.total_tokens }} tokens<span v-if="result.usage.cost && result.usage.cost.total_cost"> · ${{ result.usage.cost.total_cost.toFixed(4) }}</span>
            </span>
          </div>
          <div class="foot-buttons">
            <button class="btn-close" @click="close">Close</button>
            <button
              v-if="result && result.influencer"
              class="btn-apply"
              :disabled="applicableFields.length === 0"
              @click="apply"
              :title="applicableFields.length ? ('Apply ' + applicableFields.length + ' field(s) to the form') : 'Nothing to apply'"
            >
              <i class="bi bi-check-lg" aria-hidden="true"></i>
              Apply <span class="apply-count" v-if="applicableFields.length">{{ applicableFields.length }}</span> {{ applicableFields.length === 1 ? 'field' : 'fields' }}
            </button>
          </div>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
  import { detectPlatformHint, stripQuery } from '../../utils/socialChannels';

  export default {
    name: 'ai-suggest-modal',
    props: {
      open: { type: Boolean, default: false },
      initialName: { type: String, default: '' },
      initialFirstname: { type: String, default: '' },
      initialLastname: { type: String, default: '' },
      initialChannels: { type: Array, default: () => [] }
    },
    data() {
      return {
        apiHost: this.$appSettings.getSetting('host'),
        local: {
          name: '',
          firstname: '',
          lastname: '',
          channels: [],
          model: 'sonar-deep-research'
        },
        models: [
          { value: 'sonar',                label: 'Sonar',              badge: 'Fast',        cost: 'low',    costLabel: '$',     description: 'Quick, economical search — fewer sources, shorter answer.' },
          { value: 'sonar-pro',            label: 'Sonar Pro',          badge: '',            cost: 'medium', costLabel: '$$',    description: 'Advanced search with more sources and longer answers.' },
          { value: 'sonar-reasoning',      label: 'Sonar Reasoning',    badge: 'CoT',         cost: 'medium', costLabel: '$$',    description: 'Search combined with Chain-of-Thought reasoning.' },
          { value: 'sonar-reasoning-pro',  label: 'Sonar Reasoning Pro', badge: '',           cost: 'high',   costLabel: '$$$',   description: 'Higher-quality reasoning and inference for harder lookups.' },
          { value: 'sonar-deep-research',  label: 'Sonar Deep Research', badge: 'Default',    cost: 'highest', costLabel: '$$$$', description: 'In-depth research report. Highest cost and longest response time.' }
        ],
        loading: false,
        error: null,
        result: null
      };
    },
    computed: {
      canSearch() {
        const hasName = (this.local.name || '').trim().length > 0;
        const hasFullName = (this.local.firstname || '').trim() && (this.local.lastname || '').trim();
        const hasChannel = this.local.channels.some(c => (c.url || '').trim().length > 0);
        return hasName || hasFullName || hasChannel;
      },
      genderLabel() {
        if (!this.result || !this.result.influencer) return '';
        const g = this.result.influencer.gender;
        if (g === 1) return 'Male';
        if (g === 2) return 'Female';
        return '';
      },
      currentModel() {
        return this.models.find(m => m.value === this.local.model) || this.models[this.models.length - 1];
      },
      applicableFields() {
        if (!this.result || !this.result.influencer) return [];
        const inf = this.result.influencer;
        const fields = [];
        if (inf.firstname) fields.push('firstname');
        if (inf.lastname) fields.push('lastname');
        if (inf.nickname) fields.push('nickname');
        if (inf.location) fields.push('location');
        if (inf.email) fields.push('email');
        if (inf.site) fields.push('site');
        if (inf.gender === 0 || inf.gender === 1 || inf.gender === 2) fields.push('gender');
        if (typeof inf.age === 'number' && inf.age > 0 && inf.age < 120) fields.push('age');
        if (inf.lang && ['un', 'pl', 'bg', 'cs'].indexOf(String(inf.lang).toLowerCase()) !== -1) fields.push('lang');
        if (Array.isArray(inf.channels) && inf.channels.length > 0) fields.push('channels');
        return fields;
      }
    },
    watch: {
      open(now) {
        if (now) {
          this.resetFromInitial();
          document.addEventListener('keydown', this.onKeydown);
          document.body.style.overflow = 'hidden';
        } else {
          document.removeEventListener('keydown', this.onKeydown);
          document.body.style.overflow = '';
        }
      }
    },
    beforeDestroy() {
      document.removeEventListener('keydown', this.onKeydown);
      document.body.style.overflow = '';
    },
    methods: {
      resetFromInitial() {
        this.local.name = this.initialName || '';
        this.local.firstname = this.initialFirstname || '';
        this.local.lastname = this.initialLastname || '';
        this.local.channels = (this.initialChannels || []).map(c => ({
          url: c.url || '',
          platform: c.platform || detectPlatformHint(c.url) || ''
        }));
        if (!this.local.channels.length) {
          this.local.channels.push({ url: '', platform: '' });
        }
        this.error = null;
        this.result = null;
      },
      onKeydown(e) {
        if (e.key === 'Escape') this.close();
      },
      close() {
        this.$emit('close');
      },
      apply() {
        if (!this.result || !this.result.influencer) return;
        this.$emit('apply', this.result.influencer, this.applicableFields);
      },
      addChannel() {
        this.local.channels.push({ url: '', platform: '' });
      },
      removeChannel(idx) {
        this.local.channels.splice(idx, 1);
      },
      onChannelInput(idx) {
        const ch = this.local.channels[idx];
        const stripped = stripQuery(ch.url);
        if (stripped !== ch.url) {
          this.$set(this.local.channels, idx, { ...ch, url: stripped });
        }
        ch.platform = detectPlatformHint(ch.url) || '';
      },
      platformIconSrc(platform) {
        try {
          return require('../../assets/platforms/' + platform + '.png');
        } catch (e) {
          return '';
        }
      },
      splitCsv(s) {
        return (s || '').split(',').map(x => x.trim()).filter(Boolean);
      },
      extractUsername(url) {
        if (!url) return '';
        const match = url.match(/\/@?([A-Za-z0-9._-]+)\/?$/);
        return match ? match[1] : '';
      },
      buildPayload() {
        const body = {};
        const name = (this.local.name || '').trim();
        const firstname = (this.local.firstname || '').trim();
        const lastname = (this.local.lastname || '').trim();
        if (name) body.name = name;
        if (firstname) body.firstname = firstname;
        if (lastname) body.lastname = lastname;
        const channels = this.local.channels
          .map(c => ({
            url: (c.url || '').trim(),
            platform: c.platform || detectPlatformHint(c.url) || ''
          }))
          .filter(c => c.url.length > 0 && c.platform);
        if (channels.length) body.channels = channels;
        if (this.local.model) body.model = this.local.model;
        return body;
      },
      search() {
        if (!this.canSearch) return;
        this.loading = true;
        this.error = null;
        this.result = null;
        const payload = this.buildPayload();
        this.$http.post(this.apiHost + 'perplexity/influencer/search', payload, {
          headers: { Authorization: 'Bearer ' + this.$auth.getToken() },
          params: { token: this.$auth.getToken() }
        }).then(response => {
          this.loading = false;
          const body = response.body || {};
          if (body.success && body.data) {
            this.result = body.data;
          } else {
            this.error = body.error || 'Empty response from server';
          }
        }, response => {
          this.loading = false;
          const body = (response && response.body) || {};
          if (body.error) {
            this.error = typeof body.error === 'string' ? body.error : JSON.stringify(body.error);
          } else if (response && response.status === 400) {
            this.error = 'Missing required parameters';
          } else if (response && response.status) {
            this.error = 'HTTP ' + response.status + ' — request failed';
          } else {
            this.error = 'Network error';
          }
        });
      }
    }
  }
</script>

<style scoped>
  .modal-root {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 200;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 60px 16px 30px;
    overflow-y: auto;
  }
  .modal-card {
    width: 100%;
    max-width: 720px;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(15, 23, 42, 0.35);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 90px);
  }

  .modal-head {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 18px 20px;
    background: linear-gradient(135deg, #eaf1f9 0%, #fdf4ff 100%);
    border-bottom: 1px solid #e5e7eb;
  }
  .head-icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 11px;
    background: #ede9fe;
    color: #6d3fc4;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .head-text { flex: 1; min-width: 0; }
  .modal-title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
    letter-spacing: -0.01em;
  }
  .modal-subtitle {
    font-size: 12.5px;
    color: #6b7280;
    margin: 0;
  }
  .head-close {
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid #e5e7eb;
    color: #6b7280;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;
  }
  .head-close:hover {
    background: #fff;
    color: #0f172a;
  }

  .modal-body {
    overflow-y: auto;
    padding: 18px 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .step-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }
  .step-badge {
    width: 22px;
    height: 22px;
    border-radius: 999px;
    background: #265a88;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .step-badge-2 { background: #10b981; }
  .step-title {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .step-desc {
    font-size: 12.5px;
    color: #6b7280;
  }

  .form-group { margin-bottom: 12px; }
  .form-group:last-child { margin-bottom: 0; }
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .form-label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 5px;
  }
  .form-control {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 9px 12px;
    font-size: 13.5px;
    background: #fff;
    color: #0f172a;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .form-control:focus {
    outline: none;
    border-color: #265a88;
    box-shadow: 0 0 0 3px rgba(38, 90, 136, 0.12);
  }

  .channels-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .channel-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .channel-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 1px solid #e5e7eb;
  }
  .channel-icon.has-platform { background: #fff; }
  .channel-icon img { width: 22px; height: 22px; border-radius: 4px; }
  .channel-icon .dot { color: #6b7280; font-weight: 600; font-size: 14px; }
  .channel-row .form-control { flex: 1; }
  .remove-btn {
    background: transparent;
    border: 1px solid transparent;
    color: #6b7280;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  .remove-btn:hover {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fecaca;
  }
  .add-channel-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px;
    background: #fafbfc;
    border: 1px dashed #d1d5db;
    color: #475569;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
  }
  .add-channel-btn:hover {
    border-color: #265a88;
    color: #265a88;
    border-style: solid;
  }

  .model-group {
    margin-top: 14px;
    padding: 12px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
  }
  .model-group .form-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  .model-cost {
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.04em;
    padding: 2px 7px;
    border-radius: 999px;
  }
  .cost-low     { color: #03543f; background: #def7ec; }
  .cost-medium  { color: #1e40af; background: #dbeafe; }
  .cost-high    { color: #92400e; background: #fef3c7; }
  .cost-highest { color: #9b1c1c; background: #fde8e8; }

  .model-options {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .model-option {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 12.5px;
    color: #475569;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
    margin: 0;
    user-select: none;
  }
  .model-option input {
    appearance: none;
    -webkit-appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1.5px solid #cbd5e1;
    margin: 0;
    flex-shrink: 0;
    transition: border-color 0.15s, background 0.15s;
    position: relative;
  }
  .model-option:hover {
    border-color: #265a88;
    color: #0f172a;
  }
  .model-option.is-active {
    border-color: #265a88;
    background: #eaf1f9;
    color: #265a88;
    font-weight: 600;
  }
  .model-option.is-active input {
    border-color: #265a88;
    background: radial-gradient(#265a88 38%, transparent 42%);
  }
  .model-option-badge {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 1px 6px;
    border-radius: 999px;
    background: rgba(38, 90, 136, 0.08);
    color: #265a88;
  }
  .model-option.is-active .model-option-badge {
    background: rgba(38, 90, 136, 0.18);
  }
  .model-hint {
    margin: 8px 0 0;
    font-size: 12px;
    color: #6b7280;
    line-height: 1.4;
  }

  .btn-search {
    margin-top: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    background: linear-gradient(135deg, #265a88 0%, #833ab4 100%);
    color: #fff;
    border: 0;
    border-radius: 9px;
    padding: 11px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(38, 90, 136, 0.25);
    transition: transform 0.1s, box-shadow 0.15s, opacity 0.15s;
  }
  .btn-search:hover:not(:disabled) {
    box-shadow: 0 6px 18px rgba(38, 90, 136, 0.35);
    transform: translateY(-1px);
  }
  .btn-search:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .btn-content { display: inline-flex; align-items: center; gap: 6px; }
  .spinner {
    display: inline-block;
    width: 13px;
    height: 13px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .hint {
    margin: 8px 0 0;
    font-size: 12px;
    color: #6b7280;
    text-align: center;
  }

  .step-results { padding-top: 6px; border-top: 1px dashed #e5e7eb; }

  .result-error {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 10px;
    color: #991b1b;
  }
  .result-error strong { display: block; font-size: 13px; }
  .result-error p { margin: 4px 0 0; font-size: 12.5px; }

  .result-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .result-card {
    padding: 14px;
    background: #fafbfc;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
  }
  .result-card-main { grid-column: 1 / -1; background: linear-gradient(135deg, #eaf1f9 0%, #fdf4ff 100%); border-color: #c7dcf0; }
  .result-card-full { grid-column: 1 / -1; }

  .card-eyebrow {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #6b7280;
    margin-bottom: 8px;
  }
  .result-nickname {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    color: #0f172a;
    letter-spacing: -0.02em;
  }
  .result-realname {
    margin: 2px 0 12px;
    color: #475569;
    font-size: 13.5px;
  }
  .result-attrs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .attr {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    padding: 4px 11px 4px 9px;
    font-size: 12px;
    color: #475569;
    font-weight: 500;
  }
  .attr .bi { color: #6b7280; }

  .bio {
    margin: 0;
    color: #374151;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
  }

  .chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .chip {
    display: inline-block;
    background: #f1f5f9;
    color: #475569;
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 500;
  }
  .chip-blue {
    background: #dbeafe;
    color: #1d4ed8;
  }

  .channels-result {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .channel-pill {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px 8px 8px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 9px;
    text-decoration: none;
    color: #0f172a;
    transition: border-color 0.15s, background 0.15s;
  }
  .channel-pill:hover {
    border-color: #265a88;
    background: #f8fafc;
  }
  .channel-pill img { width: 24px; height: 24px; border-radius: 5px; }
  .channel-pill-text { display: flex; flex-direction: column; min-width: 0; flex: 1; }
  .channel-platform {
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
  }
  .channel-username {
    font-size: 13px;
    color: #0f172a;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .channel-pill .bi { color: #6b7280; flex-shrink: 0; }

  .kv-list { display: flex; flex-direction: column; gap: 6px; }
  .kv { display: grid; grid-template-columns: 70px 1fr; gap: 8px; font-size: 13px; }
  .kv-key { color: #6b7280; font-weight: 500; }
  .kv-val { color: #265a88; text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .kv-val:hover { text-decoration: underline; }

  .citations {
    margin: 0;
    padding-left: 18px;
    color: #475569;
    font-size: 12.5px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .citations a {
    color: #265a88;
    text-decoration: none;
    overflow-wrap: anywhere;
  }
  .citations a:hover { text-decoration: underline; }

  .result-empty {
    text-align: center;
    padding: 30px 16px;
    color: #6b7280;
  }
  .result-empty p { margin: 8px 0 0; font-size: 13.5px; }

  .modal-foot {
    border-top: 1px solid #e5e7eb;
    padding: 12px 20px;
    background: #fafbfc;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
  .foot-info {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #6b7280;
  }
  .foot-info .bi { color: #6b7280; flex-shrink: 0; }
  .foot-info strong { color: #92400e; font-weight: 600; }
  .usage { color: #6b7280; }
  .foot-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  .btn-close {
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #475569;
    border-radius: 8px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
  }
  .btn-close:hover { border-color: #265a88; color: #265a88; }
  .btn-apply {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: #fff;
    border: 0;
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(16, 185, 129, 0.25);
    transition: box-shadow 0.15s, transform 0.1s, opacity 0.15s;
  }
  .btn-apply:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
    transform: translateY(-1px);
  }
  .btn-apply:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .apply-count {
    background: rgba(255, 255, 255, 0.25);
    border-radius: 999px;
    padding: 1px 7px;
    font-size: 11px;
    font-weight: 700;
  }

  .modal-fade-enter-active, .modal-fade-leave-active {
    transition: opacity 0.18s ease;
  }
  .modal-fade-enter-active .modal-card, .modal-fade-leave-active .modal-card {
    transition: transform 0.18s ease, opacity 0.18s ease;
  }
  .modal-fade-enter, .modal-fade-leave-to {
    opacity: 0;
  }
  .modal-fade-enter .modal-card, .modal-fade-leave-to .modal-card {
    transform: translateY(-8px);
    opacity: 0;
  }

  @media (max-width: 640px) {
    .modal-root { padding: 16px; }
    .form-row { grid-template-columns: 1fr; }
    .result-grid { grid-template-columns: 1fr; }
    .modal-foot {
      flex-direction: column;
      align-items: stretch;
    }
    .foot-buttons {
      width: 100%;
    }
    .btn-close, .btn-apply {
      flex: 1;
      justify-content: center;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/influencer/AiSuggestModal.vue